import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { axiosPrivate } from "../../api/axios";
import UserContext from "../UserContext";
import { RiErrorWarningFill } from "react-icons/ri";

const cache = {};

export const useAccounts = ({ endpoint, param, worker }) => {
  const { userData } = useContext(UserContext);
  const [getAccount, setGetAccount] = useState([]);
  const [getPolicy, setGetPolicy] = useState([]);
  const [allPpaSummary, setAllPpaSummary] = useState([]);
  const [nullData, setNullData] = useState("");
  const [paginatedLimit, setPaginatedLimit] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [isLoading, setIsLoading] = useState(false);

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      const url =
        process.env.REACT_APP_API_URL +
        (param ? `${endpoint}/${param}` : endpoint) +
        (worker ? `/${worker}` : "");

      if (param && cache[url]) {
        delete cache[url];
      }

      if (
        !param &&
        cache[url] &&
        cache[url][currentPage] &&
        cache[url][currentPage].limit === paginatedLimit
      ) {
        return cache[url][currentPage].data;
      }

      const response = await axiosPrivate.get(url, {
        headers: {
          Authorization: `Bearer ${userData?.access_token}`,
        },
        params: {
          limit: paginatedLimit,
          page: currentPage,
        },
      });

      const result = response?.data;

      if (!param) {
        if (!cache[url]) {
          cache[url] = {};
        }
        cache[url][currentPage] = {
          data: result,
          limit: paginatedLimit,
        };
      }

      return result;
    } catch (error) {
      if (error.response?.status === 401) {
        // Clear cache on unauthorized
        Object.keys(cache).forEach((key) => delete cache[key]);
      }
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, [endpoint, param, userData?.access_token, paginatedLimit, currentPage]);

  useEffect(() => {
    let isMounted = true;

    const loadData = async () => {
      try {
        const res = await fetchData();

        if (!isMounted) return;

        const allData = res.data || [];
        const totalCount = res.totalItems || allData.length;
        const pages = res.totalPages || Math.ceil(totalCount / paginatedLimit);

        setGetAccount(allData);
        setAllPpaSummary(allData);
        setGetPolicy(allData);
        setTotalItems(totalCount);
        setTotalPages(pages);

        if (allData.length < 1) {
          setNullData(
            <div className="tw-py-10 tw-px-4 tw-flex tw-flex-col tw-items-center tw-justify-center tw-gap-4">
              <h6>No Data Available</h6>
              <span className="tw-text-7xl">&#x1F4C2;</span>
            </div>
          );
        } else {
          setNullData("");
        }
      } catch (error) {
        if (!isMounted) return;

        setNullData(
          <div className="tw-py-10 tw-flex tw-flex-col tw-items-center tw-justify-center tw-gap-y-1">
            <div className="tw-items-center tw-flex tw-flex-col">
              <RiErrorWarningFill className="tw-text-red-500 tw-text-[36px]" />
              <h5>Error loading accounts</h5>
            </div>
            <p>{error.message}</p>
          </div>
        );
        setGetAccount([]);
      }
    };

    loadData();

    return () => {
      isMounted = false;
    };
  }, [fetchData, paginatedLimit]);

  const currentItems = useMemo(() => {
    return getAccount;
  }, [getAccount]);

  const itemsPerPage = currentItems.length;

  const handlePageChange = useCallback((newPage) => {
    setCurrentPage(newPage);
  }, []);

  const handleLimitChange = useCallback((newLimit) => {
    setPaginatedLimit(Number(newLimit));
    setCurrentPage(1);
  }, []);

  return {
    getAccount,
    currentItems,
    allPpaSummary,
    getPolicy,
    nullData,
    isLoading,
    itemsPerPage,
    paginatedLimit,
    currentPage,
    totalPages,
    totalItems,
    onLimitChange: handleLimitChange,
    onPageChange: handlePageChange,
  };
};
